import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";

// Redirecionamento pós-login por perfil (mantido intacto)
const _destinoLogin = (role) => {
  if (role === "cliente") return "/cliente/home";
  if (role === "vendedor") return "/app/meu-dashboard";
  return "/app/dashboard";
};

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  // Sessão vencida: aviso neutro, sem bloqueio
  const sessaoEncerrada = new URLSearchParams(window.location.search).get("expired") === "1";

  // Retorno do Emergent Google Auth
  useEffect(() => {
    if (!window.location.hash?.includes("session_id=")) return;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    if (!sid) return;
    setGoogleBusy(true);
    (async () => {
      try {
        const { data } = await api.post("/auth/google-session", { session_id: sid });
        localStorage.setItem("lab_token", data.token);
        localStorage.setItem("lab_user", JSON.stringify(data.user));
        if (data.filial_ativa) localStorage.setItem("filial_selecionada", data.filial_ativa);
        toast.success(`Bem-vindo(a), ${data.user.name}`);
        window.history.replaceState(null, "", window.location.pathname);
        window.location.href = _destinoLogin(data.user.role);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Falha no login Google");
        setGoogleBusy(false);
        window.history.replaceState(null, "", window.location.pathname);
      }
    })();
  }, []);

  if (user) {
    if (user.senha_temporaria) return <Navigate to="/app/trocar-senha" replace />;
    return <Navigate to={_destinoLogin(user.role)} replace />;
  }

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const data = await login(email.trim(), password);
      const u = data.user || data;
      toast.success(`Bem-vindo(a), ${u.name}`);
      if (u.senha_temporaria) {
        toast.warning("Você está usando uma senha temporária. Defina uma nova senha agora.", { duration: 5000 });
        nav("/app/trocar-senha", { replace: true });
        return;
      }
      if (data.geo_divergencia) {
        toast.warning("📍 Você está acessando de fora da sua filial habitual.", { duration: 5000 });
      } else if (data.geo_detectada) {
        toast.info("📍 Filial detectada automaticamente", { duration: 3000 });
      }
      nav(u.role === "cliente" ? "/cliente/home" : "/app/dashboard", { replace: true });
    } catch (err) {
      const detailRaw = err?.response?.data?.detail;
      const detail = typeof detailRaw === "string"
        ? detailRaw
        : (detailRaw?.mensagem || detailRaw?.msg || "E-mail ou senha incorretos.");
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const entrarComGoogle = () => {
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const esqueciSenha = (e) => {
    e.preventDefault();
    toast.info(
      "Solicite o reset ao administrador da sua empresa ou ao consultor LAB. Uma nova senha temporária será gerada.",
      { duration: 8000 }
    );
  };

  return (
    <div className="precision-dark min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* ============================================================ */}
      {/* PAINEL ESQUERDO (55%) — Dark · Branding · Credibilidade      */}
      {/* ============================================================ */}
      <section className="pd-left relative overflow-hidden flex flex-col justify-between px-8 sm:px-12 lg:px-16 py-12 lg:py-16">
        {/* Círculos concêntricos decorativos */}
        <span className="pd-orb pd-orb-1" aria-hidden="true" />
        <span className="pd-orb pd-orb-2" aria-hidden="true" />

        {/* TOPO — Brand */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="pd-brand-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
              <line x1="5" y1="7" x2="19" y2="7" />
              <line x1="5" y1="12" x2="19" y2="12" />
              <line x1="5" y1="17" x2="14" y2="17" />
            </svg>
          </div>
          <div>
            <div className="pd-brand-name">
              Locar <span>Ativo</span> Brasil
            </div>
            <div className="pd-brand-url">locarativosbrasil.com.br</div>
          </div>
        </div>

        {/* MEIO — Hero */}
        <div className="relative z-10 max-w-xl lg:hidden xl:block">
          <div className="pd-tag">Plataforma Enterprise</div>
          <h1 className="pd-hero">
            Gerencie sua frota com<strong>precisão total.</strong>
          </h1>
          <p className="pd-hero-sub">
            A plataforma unificada para locação industrial — controle operacional,
            comercial e financeiro com confiabilidade enterprise.
          </p>
        </div>

        {/* Versão compacta do hero para telas médias */}
        <div className="relative z-10 max-w-xl hidden lg:block xl:hidden">
          <div className="pd-tag">Plataforma Enterprise</div>
          <h1 className="pd-hero pd-hero-compact">
            Gestão de frota com<strong>precisão total.</strong>
          </h1>
        </div>

        {/* RODAPÉ — Métricas de credibilidade */}
        <div className="relative z-10">
          <div className="pd-divider" aria-hidden="true" />
          <div className="pd-metrics">
            <div className="pd-metric">
              <div className="pd-metric-value">98<span>%</span></div>
              <div className="pd-metric-label">uptime</div>
            </div>
            <div className="pd-metric">
              <div className="pd-metric-value">24<span>h</span></div>
              <div className="pd-metric-label">suporte</div>
            </div>
            <div className="pd-metric">
              <div className="pd-metric-value">100<span>+</span></div>
              <div className="pd-metric-label">clientes ativos</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* PAINEL DIREITO (45%) — Formulário                            */}
      {/* ============================================================ */}
      <section className="pd-right flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 py-12">
        <div className="pd-form-wrap w-full max-w-sm">
          <div className="mb-8">
            <h2 className="pd-form-title">Entrar na plataforma</h2>
            <p className="pd-form-sub">Use suas credenciais corporativas para acessar.</p>
            {sessaoEncerrada && (
              <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="login-sessao-encerrada">
                Sessão encerrada. Faça login novamente
              </div>
            )}
          </div>

          {googleBusy && (
            <div className="pd-notice pd-notice-info mb-5" role="status">
              <Loader2 className="animate-spin shrink-0" size={14} />
              Validando autenticação Google…
            </div>
          )}

          <form onSubmit={submit} className="space-y-5" noValidate>
            <div>
              <label htmlFor="email" className="pd-label">E-mail corporativo</label>
              <input
                id="email"
                data-testid="login-email-input"
                aria-label="E-mail corporativo ou nome cadastrado do cliente"
                type="text"
                placeholder="voce@empresa.com.br"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required autoComplete="username"
                className="pd-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="pwd" className="pd-label !mb-0">Senha</label>
                <button
                  type="button"
                  onClick={esqueciSenha}
                  className="pd-forgot-link"
                  data-testid="login-forgot-link"
                >
                  Esqueci minha senha
                </button>
              </div>
              <input
                id="pwd"
                data-testid="login-password-input"
                aria-label="Senha"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required autoComplete="current-password"
                className="pd-input"
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="pd-btn-primary group"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Autenticando…
                </>
              ) : (
                <>
                  Acessar plataforma
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          <div className="pd-separator">
            <span>ou</span>
          </div>

          <button
            type="button"
            onClick={entrarComGoogle}
            disabled={googleBusy}
            data-testid="login-google-button"
            className="pd-btn-google"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Entrar com Google
          </button>

          <p className="pd-foot">Apenas para e-mails autorizados no LAB.</p>
        </div>
      </section>
    </div>
  );
}
